import React from "react";
import PropTypes from "prop-types";
import { IconButton, Portal, GlobalKeyPress, ErrorBoundary, EmptyStateMessage } from "@evertrue/et-components";
import classNames from "classnames";
import { Transition } from "react-transition-group";
import { useChatNavigationContext } from "../contexts/chat-navigation-context";
import { useChatRoomSubscription } from "../contexts/chat-room-subscription-context";
import ChatRoutes from "./chat-routes";
import Header from "./header";
import { useChatDependencyContext } from "../contexts/chat-dependency-context";

const TIMEOUT = 200;
const UNREAD_LIMIT = 999;
const ChatMain = ({ className, transitionState, close, dispatch, onChatError }) => (
  <div className={classNames("chat-panel", className, transitionState)}>
    <GlobalKeyPress onEscape={close} />
    <ErrorBoundary
      onError={onChatError}
      FallbackComponent={() => (
        <>
          <Header title="Chat" />
          <EmptyStateMessage
            className="chat-panel--error-boundary"
            icon={"build"}
            text="Oops, there was an error with chat, click below to reset your chat."
            actionLabel="Reset Chat"
            onAction={() => dispatch({ type: "RESET_CHAT", payload: true })}
          />
        </>
      )}
    >
      {/* Transition mounts and unmounts so put Keypress here so event listener gets removed when not mounted */}
      <ChatRoutes />
    </ErrorBoundary>
  </div>
);
const ChatOpenButton = ({ onClick = () => {}, className, buttonClassName, onChatError }) => {
  const [nav_state, dispatch] = useChatNavigationContext();
  const { numUnread } = useChatRoomSubscription();
  const { isMobile, mixPanelEvents } = useChatDependencyContext();

  const max_num_unread = numUnread > UNREAD_LIMIT ? `${UNREAD_LIMIT}+` : numUnread;

  const close = React.useCallback(() => dispatch({ type: "CLOSE_PANEL" }), [dispatch]);

  return (
    <div className="chat-panel--wrapper">
      <IconButton
        className={classNames("chat-panel--button", buttonClassName)}
        icon="appchat"
        title="chat"
        size={1}
        onClick={() => {
          mixPanelEvents.viewChatPanel();
          onClick();
          dispatch({ type: "OPEN_PANEL" });
        }}
      >
        {Boolean(max_num_unread) && <span className="chat-panel--unread-indicator">{max_num_unread}</span>}
      </IconButton>
      {!isMobile ? (
        <Portal>
          {nav_state.panel_is_open && <div className="chat-panel--overlay" onClick={close} />}
          <Transition in={nav_state.panel_is_open} timeout={TIMEOUT} appear={true} unmountOnExit mountOnEnter>
            {transitionState => (
              <ChatMain
                className={className}
                transitionState={transitionState}
                close={close}
                dispatch={dispatch}
                onChatError={onChatError}
              />
            )}
          </Transition>
        </Portal>
      ) : nav_state.panel_is_open ? (
        <Portal>
          <ChatMain className={className} close={close} dispatch={dispatch} onChatError={onChatError} />
        </Portal>
      ) : null}
    </div>
  );
};

ChatOpenButton.propTypes = {
  onClick: PropTypes.func,
  buttonClassName: PropTypes.string,
  onChatError: PropTypes.func,
  className: PropTypes.string,
};

export default ChatOpenButton;
